import React from 'react';

/**
 * iter121 — Unified badge hierarchy.
 *
 * Three visual tiers sharing an identical shield+tick shape / layout;
 * only the shield colour and title differ per the product spec:
 *   Verified User  → BLUE   (#2563eb)  every mobile-OTP-verified account
 *   Trusted Seller → ORANGE (#f97316)  ₹199 / ₹399 individual premium plans
 *   Trusted Dealer → GREEN  (#00bf63)  every dealer plan
 *
 * Priority (highest → lowest): Trusted Dealer > Trusted Seller > Verified User > No badge.
 *
 * Use `<SellerBadge trust={...} />` — it takes an object with the raw flags
 * from the user/listing record and renders ONLY the single highest tier
 * that applies, enforcing the never-two-badges-at-once rule.
 */

const TIERS = {
  dealer:  { title: 'Trusted Dealer',  color: '#00bf63' },
  seller:  { title: 'Trusted Seller',  color: '#f97316' },
  user:    { title: 'Verified User',   color: '#2563eb' },
};

const SIZE_MAP = {
  xs: { pad: 'px-1.5 py-0.5 text-[9px] gap-1',   icon: 10 },
  sm: { pad: 'px-2 py-0.5 text-[10px] gap-1',    icon: 12 },
  md: { pad: 'px-2.5 py-1 text-xs gap-1.5',      icon: 14 },
  lg: { pad: 'px-3 py-1.5 text-sm gap-1.5',      icon: 16 },
};

const ShieldTick = ({ px = 12, color = '#fff' }) => (
  <svg width={px} height={px} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M12 1L3 5v6c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V5l-9-4zm-1.4 15.2L7 12.6l1.4-1.4 2.2 2.2 5.6-5.6L17.6 9.2l-7 7z" />
  </svg>
);

/**
 * @param {'dealer'|'seller'|'user'|null} tier   Explicit tier to render. If
 *   null / undefined, the component renders nothing.
 * @param {'xs'|'sm'|'md'|'lg'} size            Visual size.
 * @param {string} testId                       Test id override.
 */
const BadgeTier = ({ tier, size = 'sm', testId }) => {
  if (!tier || !TIERS[tier]) return null;
  const meta = TIERS[tier];
  const cfg = SIZE_MAP[size] || SIZE_MAP.sm;
  return (
    <span
      data-testid={testId || `badge-${tier}`}
      title={meta.title}
      className={`inline-flex items-center rounded-full font-bold text-white shadow-sm uppercase tracking-wide ${cfg.pad}`}
      style={{ backgroundColor: meta.color }}
    >
      <ShieldTick px={cfg.icon} />
      {meta.title}
    </span>
  );
};

/**
 * Given the trust flags from a user or listing, resolve the ONE highest tier
 * and render only that badge. Enforces the never-two-badges-at-once rule.
 *
 * Expected flags (all optional / boolean):
 *   • is_dealer / is_dealer_listing / dealer_verified — true ⇒ Trusted Dealer
 *   • is_trusted_seller                              — true ⇒ Trusted Seller
 *   • phone_verified                                 — true ⇒ Verified User
 */
export const SellerBadge = ({ trust = {}, size = 'sm', testId }) => {
  const t = trust || {};
  let tier = null;
  if (t.is_dealer || t.is_dealer_listing || t.dealer_verified) tier = 'dealer';
  else if (t.is_trusted_seller) tier = 'seller';
  else if (t.phone_verified) tier = 'user';
  return <BadgeTier tier={tier} size={size} testId={testId} />;
};

export default BadgeTier;
